import { Immutable } from "immer";
import React from "react";
import { RawHTMLBlock } from "tsi-common-react/src/components/RawHTMLBlock/index";
import { useRovingTabList } from "tsi-common-react/src/hooks/useRovingTabList";

import type { TabNav as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { StreamField } from "@thelabnyc/thelabui/src/components/StreamField";

import { AccordionSection } from "../AccordionSection";

type Props = Immutable<{
    value: Value;
}>;

/**
 * TabNav renders a tabbed-content block used on utility pages. Tab switching is
 * owned here via the shared ``useRovingTabList`` hook plus component state: the
 * active tab carries ``tabs__tab--is-active`` (+ ``aria-selected="true"`` /
 * roving ``tabIndex=0``) and its pane carries ``tabs__pane--is-active``. Those
 * ``--is-active`` classes are load-bearing -- ``.tabs__pane`` is ``display:none``
 * until ``--is-active`` (see ``_tabs.scss``), so the class is what reveals a
 * pane. ``activeIndex`` starts at 0 so the first tab/pane render active, keeping
 * the SSR markup identical to the hydrated DOM.
 *
 * The anchor's ``href`` and the pane ``id`` share the 1-based counter for a
 * no-JS fragment fallback and the ``aria-controls`` / ``aria-labelledby``
 * pairing; the hook's click handler prevents the default fragment navigation.
 *
 * Each tab's body is itself a Wagtail StreamBlock with five possible inner
 * block types (``heading``, ``paragraph``, ``image``, ``html``,
 * ``accordion_section``) dispatched through thelabui's ``<StreamField>``.
 */
export const TabNav = (props: Props): React.JSX.Element | null => {
    const v = props.value;
    const sections = v.sections ?? [];
    const { activeIndex, getTabProps } = useRovingTabList({
        count: sections.length,
    });
    // Per-instance id prefix so the tab/pane ids stay unique when a page's
    // StreamField renders more than one `tab_nav` block (otherwise every block
    // emits `tab-1`/`1` and the second block's aria-controls/aria-labelledby/
    // fragment links resolve to the first). useId is SSR-stable, so the no-JS
    // fragment fallback (`href="#<prefix>1"` -> pane `id="<prefix>1"`) still
    // matches across server render and hydration.
    const idPrefix = React.useId();
    return (
        <div className="tabs">
            <div className="tabs__nav">
                <ul className="tabs__tab-list" role="tablist">
                    {sections.map((section, i) => (
                        <li key={i} className="tabs__tab-list-item">
                            <a
                                id={`${idPrefix}tab-${i + 1}`}
                                href={`#${idPrefix}${i + 1}`}
                                className={
                                    i === activeIndex
                                        ? "tabs__tab tabs__tab--is-active"
                                        : "tabs__tab"
                                }
                                role="tab"
                                aria-controls={`${idPrefix}${i + 1}`}
                                {...getTabProps(i)}
                            >
                                {section.title}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="tabs__content">
                {sections.map((section, i) => (
                    <div
                        key={i}
                        id={`${idPrefix}${i + 1}`}
                        className={
                            i === activeIndex
                                ? "tabs__pane tabs__pane--is-active"
                                : "tabs__pane"
                        }
                        role="tabpanel"
                        aria-labelledby={`${idPrefix}tab-${i + 1}`}
                        tabIndex={0}
                    >
                        <StreamField
                            tagName={React.Fragment}
                            value={section.body ?? []}
                            components={{
                                heading: (block) => (
                                    <h2 className="full title">
                                        {block.value}
                                    </h2>
                                ),
                                paragraph: (block) => (
                                    <RichTextBlock value={block.value} />
                                ),
                                image: (block) => (
                                    <img
                                        src={block.value.url}
                                        alt={block.value.default_alt_text}
                                        width={block.value.width}
                                        height={block.value.height}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                ),
                                html: (block) => (
                                    <RawHTMLBlock value={block.value} />
                                ),
                                accordion_section: (block) => (
                                    <AccordionSection value={block.value} />
                                ),
                            }}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
};
